/**
 * The incident organisation as a chart, read-only, for the two forms that
 * print it: box 9 of the ICS-201 and box 4 of the ICS-207.
 *
 * The roster page renders the same chart beside its table and its member
 * actions. A form box wants the chart alone, so this fetches the roster and
 * renders just that. Seats are assigned in one place, on the roster page, and
 * each caller says how a reader gets there rather than editing the
 * organisation from inside a form.
 */

import React from 'react';
import type { ReactNode } from 'react';

import OrganizationChart from 'components/IncidentConfig/OrganizationChart';
import { useActualIncidentId } from 'components/IAP/hooks/commonHooks';
import { useGetDraftRosterMembersForIncident } from 'components/IAP/hooks/incidentRoster/useGetDraftRosterMembersForIncident';
import { useGetRosterForIncident } from 'hooks/incident/useRoster';

export const RosterOrgChartBox: React.FC<{
  /** Said when no seat has been filled, which each form phrases by its own box. */
  emptyMessage: string;
  /** Shown under the chart and under the empty message alike, e.g. a link to the roster. */
  footer?: ReactNode;
}> = ({ emptyMessage, footer }) => {
  const incidentId = useActualIncidentId();
  const { data: rosterData = [], isLoading } =
    useGetRosterForIncident(incidentId);
  const { data: draftRoster, isLoading: isDraftLoading } =
    useGetDraftRosterMembersForIncident();

  // An empty array once loading has settled means no draft was ever started, which the chart reads as null.
  const draftRosterData =
    !isDraftLoading && (!draftRoster || draftRoster.length === 0)
      ? null
      : (draftRoster ?? null);

  if (isLoading) {
    return (
      <div className="tw:text-sm tw:text-muted-foreground">
        Loading the incident organisation...
      </div>
    );
  }

  if (rosterData.length === 0) {
    return (
      <div className="tw:flex tw:min-w-0 tw:flex-col tw:gap-3">
        <p className="tw:text-sm tw:text-muted-foreground">{emptyMessage}</p>
        {footer}
      </div>
    );
  }

  return (
    <div className="tw:flex tw:min-w-0 tw:flex-col tw:gap-3">
      {/* `simplified` drops the authoring chrome the roster page needs; `readOnly` is what makes the seats inert. */}
      <OrganizationChart
        incidentMembers={rosterData}
        draftIncidentMembers={draftRosterData}
        simplified
        readOnly
      />
      {footer}
    </div>
  );
};

export default RosterOrgChartBox;
